import {
  ArrowDown,
  ArrowUp,
  Download,
  Edit3,
  Filter,
  GripVertical,
  ListOrdered,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useTheme } from "../../contexts/ThemeContext";
import formService from "../../services/formService";
import { AdminFormResponse, CreateFormQuestionRequest, FormQuestion } from "../../types/form";

const QuestionModal = React.lazy(() => import("../../components/QuestionModal"));
const ManageAnswersTab = React.lazy(() => import("./ManageAnswersTab"));

type TabType = "questions" | "answers" | "manage";

const QuestionsPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [activeTab, setActiveTab] = useState<TabType>("questions");

  const [questions, setQuestions] = useState<FormQuestion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [filterCategory, setFilterCategory] = useState<string>("");
  const [filterSubcategory, setFilterSubcategory] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortKey, setSortKey] = useState<"order" | "createdDateUtc">("order");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedQuestion, setSelectedQuestion] = useState<FormQuestion | null>(null);
  // Reorder mode state
  const [reorderMode, setReorderMode] = useState<boolean>(false);
  const [originalOrders, setOriginalOrders] = useState<Map<number, number>>(new Map());
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [savingOrder, setSavingOrder] = useState<boolean>(false);

  // Confirmation modal state
  const [reorderConfirmOpen, setReorderConfirmOpen] = useState<boolean>(false);
  const [reorderChangesCount, setReorderChangesCount] = useState<number>(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [questionToDelete, setQuestionToDelete] = useState<FormQuestion | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState<boolean>(false);

  const assignReorderedOrders = (idsInOrder: number[]) => {
    if (idsInOrder.length === 0) return;
    setQuestions(prev => {
      const existingOrders = idsInOrder.map(id => prev.find(item => item.id === id)?.order ?? 0).sort((a, b) => a - b);
      let lastKnown = existingOrders[existingOrders.length - 1] ?? 0;
      const orderMap = new Map<number, number>();
      idsInOrder.forEach((id, index) => {
        let value = existingOrders[index];
        if (value === undefined) {
          lastKnown += 1;
          value = lastKnown;
        }
        orderMap.set(id, value);
      });
      return prev.map(item => (orderMap.has(item.id) ? { ...item, order: orderMap.get(item.id)! } : item));
    });
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await formService.listQuestions({
        category: filterCategory || undefined,
        subcategory: filterSubcategory || undefined,
      });
      setQuestions(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [filterCategory, filterSubcategory]);

  const categories = useMemo(() => {
    const s = new Set<string>();
    questions.forEach(q => s.add(q.category));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [questions]);

  const categoryToSubcategories = useMemo(() => {
    const map = new Map<string, Set<string>>();
    questions.forEach(q => {
      if (!map.has(q.category)) map.set(q.category, new Set<string>());
      map.get(q.category)!.add(q.subcategory);
    });
    const obj: Record<string, string[]> = {};
    map.forEach((set, cat) => {
      obj[cat] = Array.from(set.values()).sort((a, b) => a.localeCompare(b));
    });
    return obj;
  }, [questions]);

  const subcategories = useMemo(() => {
    const s = new Set<string>();
    questions.forEach(q => {
      if (!filterCategory || q.category === filterCategory) {
        s.add(q.subcategory);
      }
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [questions, filterCategory]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let arr = questions.filter(item => {
      const catOk = !filterCategory || item.category === filterCategory;
      const subOk = !filterSubcategory || item.subcategory === filterSubcategory;
      const textOk =
        !q ||
        item.question.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.subcategory.toLowerCase().includes(q) ||
        item.type.toLowerCase().includes(q);
      return catOk && subOk && textOk;
    });
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "order") {
        cmp = (a.order ?? 0) - (b.order ?? 0);
      } else {
        cmp = new Date(a.createdDateUtc).getTime() - new Date(b.createdDateUtc).getTime();
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [questions, filterCategory, filterSubcategory, searchQuery, sortKey, sortDirection]);

  const openCreateModal = () => {
    setSelectedQuestion(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const openEditModal = (q: FormQuestion) => {
    setSelectedQuestion(q);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleSave = async (data: CreateFormQuestionRequest | Partial<CreateFormQuestionRequest>) => {
    const targetOrder = data.order ?? 0;

    if (modalMode === "create") {
      // When creating a new question, check if the order is already in use
      const existingAtOrder = questions.find(q => q.order === targetOrder);
      if (existingAtOrder) {
        // Shift all questions at or after this order by +1 to make room
        const toShift = questions.filter(q => (q.order ?? 0) >= targetOrder);
        // Sort by order descending to avoid conflicts during shifting
        toShift.sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
        for (const q of toShift) {
          await formService.updateQuestion(q.id, { order: (q.order ?? 0) + 1 });
        }
      }
      await formService.createQuestion(data as CreateFormQuestionRequest);
    } else if (modalMode === "edit" && selectedQuestion) {
      const originalOrder = selectedQuestion.order ?? 0;

      // When editing, only shift if the order has changed and the new order is already in use
      if (targetOrder !== originalOrder) {
        const existingAtOrder = questions.find(q => q.id !== selectedQuestion.id && q.order === targetOrder);
        if (existingAtOrder) {
          // Shift all questions at or after this order by +1 (excluding the question being edited)
          const toShift = questions.filter(q => q.id !== selectedQuestion.id && (q.order ?? 0) >= targetOrder);
          // Sort by order descending to avoid conflicts during shifting
          toShift.sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
          for (const q of toShift) {
            await formService.updateQuestion(q.id, { order: (q.order ?? 0) + 1 });
          }
        }
      }

      await formService.updateQuestion(selectedQuestion.id, data);
    }
    await fetchQuestions();
  };

  const handleDelete = async (id: number) => {
    await formService.deleteQuestion(id);
    await fetchQuestions();
  };

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;
    try {
      setDeletingQuestion(true);
      await handleDelete(questionToDelete.id);
      setDeleteConfirmOpen(false);
      setQuestionToDelete(null);
    } finally {
      setDeletingQuestion(false);
    }
  };

  const toggleSort = (key: "order" | "createdDateUtc") => {
    if (sortKey === key) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  // Reorder helpers
  const beginReorder = () => {
    // Force order-based view for clarity
    setSortKey("order");
    setSortDirection("asc");
    const snapshot = new Map<number, number>();
    filtered.forEach(q => snapshot.set(q.id, q.order ?? 0));
    setOriginalOrders(snapshot);
    setReorderMode(true);
  };

  const cancelReorder = () => {
    if (originalOrders.size > 0) {
      setQuestions(prev => prev.map(q => (originalOrders.has(q.id) ? { ...q, order: originalOrders.get(q.id)! } : q)));
    }
    setReorderMode(false);
    setOriginalOrders(new Map());
    setDragIndex(null);
  };

  const saveReorder = () => {
    // Collect changed items within the snapshot
    const changes = filtered.filter(q => originalOrders.get(q.id) !== (q.order ?? 0));

    if (changes.length === 0) {
      setReorderMode(false);
      setOriginalOrders(new Map());
      return;
    }

    // Show confirmation modal
    setReorderChangesCount(changes.length);
    setReorderConfirmOpen(true);
  };

  const confirmSaveReorder = async () => {
    const changes = filtered.filter(q => originalOrders.get(q.id) !== (q.order ?? 0));

    try {
      setSavingOrder(true);
      setReorderConfirmOpen(false);
      const tasks = changes.map(q => () => formService.updateQuestion(q.id, { order: q.order }));

      // Limit concurrency to 5
      const limit = 5;
      let i = 0;
      const runNext = async (): Promise<void> => {
        if (i >= tasks.length) return;
        const idx = i++;
        await tasks[idx]().catch(() => {
          // Keep UI optimistic; surface errors if needed
        });
        return runNext();
      };
      const runners = Array.from({ length: Math.min(limit, tasks.length) }, () => runNext());
      await Promise.all(runners);

      setOriginalOrders(new Map());
      setReorderMode(false);
    } finally {
      setSavingOrder(false);
    }
  };

  return (
      <AdminPageLayout
        title="Form Questions"
        description="Manage questions and view user answers"
        actions={
          activeTab === "questions" ? (
             <div className="flex items-center gap-2">
                {reorderMode && (
                  <>
                    <button
                      onClick={saveReorder}
                      disabled={savingOrder}
                      className={`px-3 py-2 rounded-lg transition-colors flex items-center ${
                        isDarkMode ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-blue-600 text-white hover:bg-blue-700"
                      } disabled:opacity-50`}
                    >
                      {savingOrder ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save
                    </button>
                    <button
                      onClick={cancelReorder}
                      disabled={savingOrder}
                      className={`px-3 py-2 rounded-lg transition-colors flex items-center ${
                        isDarkMode
                          ? "bg-rp-surface text-rp-grey hover:bg-rp-surface-raised"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </>
                )}
                {!reorderMode && (
                    <button
                        onClick={fetchQuestions}
                        disabled={loading}
                        className={`px-3 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center ${
                          isDarkMode
                            ? "text-rp-grey/70 hover:text-rp-grey hover:bg-rp-surface-raised"
                            : "text-gray-600 hover:text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                )}
                {!reorderMode && (
                    <button
                        onClick={openCreateModal}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Question
                    </button>
                )}
             </div>
          ) : null
        }
      >

      {/* Tab Navigation */}
      <div className={`mb-6 border-b ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
        <nav className="flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab("questions")}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "questions"
                ? isDarkMode
                  ? "border-rp-amber text-rp-amber"
                  : "border-blue-500 text-blue-600"
                : isDarkMode
                ? "border-transparent text-rp-grey/50 hover:text-rp-grey/70 hover:border-rp-separator"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Questions
          </button>
          <button
            onClick={() => setActiveTab("answers")}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "answers"
                ? isDarkMode
                  ? "border-rp-amber text-rp-amber"
                  : "border-blue-500 text-blue-600"
                : isDarkMode
                ? "border-transparent text-rp-grey/50 hover:text-rp-grey/70 hover:border-rp-separator"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Answers
          </button>
          <button
            onClick={() => setActiveTab("manage")}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "manage"
                ? isDarkMode
                  ? "border-rp-amber text-rp-amber"
                  : "border-blue-500 text-blue-600"
                : isDarkMode
                ? "border-transparent text-rp-grey/50 hover:text-rp-grey/70 hover:border-rp-separator"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Manage Answers
          </button>
        </nav>
      </div>

      {/* Questions Tab */}
      {activeTab === "questions" && (
        <>
      <div className={`mb-4 grid grid-cols-1 md:grid-cols-4 gap-3`}>
        <div className="flex items-center gap-2">
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search question, type, category..."
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
              isDarkMode ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey placeholder-rp-grey/40" : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900"
            }`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`} />
          <select
            value={filterCategory}
            onChange={e => {
              setFilterCategory(e.target.value);
              setFilterSubcategory(""); // reset subcategory when category changes
            }}
            className={`w-full px-2 py-2 text-sm border rounded-md ${
              isDarkMode ? "bg-rp-surface border-rp-separator text-rp-grey" : "bg-white border-gray-300 text-gray-900"
            }`}
          >
            <option value="">All categories</option>
            {categories.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Filter className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`} />
          <select
            value={filterSubcategory}
            onChange={e => setFilterSubcategory(e.target.value)}
            className={`w-full px-2 py-2 text-sm border rounded-md ${
              isDarkMode ? "bg-rp-surface border-rp-separator text-rp-grey" : "bg-white border-gray-300 text-gray-900"
            }`}
          >
            <option value="">All subcategories</option>
            {subcategories.map(sc => (
              <option key={sc} value={sc}>
                {sc}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <ListOrdered className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`} />
          <select
            value={`${sortKey}:${sortDirection}`}
            onChange={e => {
              const [key, dir] = e.target.value.split(":") as [typeof sortKey, typeof sortDirection];
              setSortKey(key);
              setSortDirection(dir);
            }}
            className={`w-full px-2 py-2 text-sm border rounded-md ${
              isDarkMode ? "bg-rp-surface border-rp-separator text-rp-grey" : "bg-white border-gray-300 text-gray-900"
            }`}
          >
            <option value="order:asc">Order ↑</option>
            <option value="order:desc">Order ↓</option>
            <option value="createdDateUtc:desc">Newest first</option>
            <option value="createdDateUtc:asc">Oldest first</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div
            className={`animate-spin rounded-full h-10 w-10 border-b-2 ${
              isDarkMode ? "border-rp-amber" : "border-blue-600"
            }`}
          ></div>
        </div>
      ) : error ? (
        <div
          className={`p-4 rounded-lg ${
            isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
          }`}
        >
          <p className={`${isDarkMode ? "text-red-300" : "text-red-600"} text-sm`}>{error}</p>
        </div>
      ) : (
        <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} shadow rounded-lg`}>
          <div className={`px-6 py-4 border-b ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
            <h3 className={`text-lg font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Questions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
              <thead className={isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}>
                <tr>
                  <th
                    className={`px-3 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    }`}
                  >
                    Reorder
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    } cursor-pointer select-none`}
                    onClick={() => toggleSort("order")}
                  >
                    Order {sortKey === "order" ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    }`}
                  >
                    Question
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    }`}
                  >
                    Category
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    }`}
                  >
                    Subcategory
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    }`}
                  >
                    Type
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    } cursor-pointer select-none`}
                    onClick={() => toggleSort("createdDateUtc")}
                  >
                    Created {sortKey === "createdDateUtc" ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    }`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDarkMode ? "bg-rp-surface divide-rp-separator" : "bg-white divide-gray-200"} divide-y`}>
                {filtered.map((q, idx) => (
                  <tr
                    key={q.id}
                    className={`${isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"} ${
                      dragIndex === idx && reorderMode ? (isDarkMode ? "bg-blue-900/20" : "bg-blue-50") : ""
                    }`}
                    draggable={reorderMode}
                    onDragStart={() => setDragIndex(idx)}
                    onDragOver={e => {
                      e.preventDefault();
                    }}
                    onDrop={() => {
                      if (dragIndex === null || dragIndex === idx) return;
                      const ids = filtered.map(item => item.id);
                      const next = [...ids];
                      const [removed] = next.splice(dragIndex, 1);
                      next.splice(idx, 0, removed);
                      assignReorderedOrders(next);
                      setDragIndex(null);
                    }}
                  >
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onMouseDown={() => {
                            if (!reorderMode) beginReorder();
                          }}
                          draggable={reorderMode}
                          className={`p-1 rounded transition-colors ${
                            isDarkMode
                              ? "text-rp-grey/50 hover:text-rp-grey/70 hover:bg-rp-surface-raised"
                              : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                          }`}
                          style={{ cursor: "grab" }}
                          title="Drag to reorder"
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (!reorderMode) beginReorder();
                            const ids = filtered.map(item => item.id);
                            const index = idx;
                            if (index <= 0) return;
                            const next = [...ids];
                            const [removed] = next.splice(index, 1);
                            next.splice(index - 1, 0, removed);
                            assignReorderedOrders(next);
                          }}
                          className={`p-1 rounded transition-colors ${
                            isDarkMode
                              ? "text-rp-grey/50 hover:text-rp-grey/70 hover:bg-rp-surface-raised"
                              : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                          }`}
                          title="Move up"
                          disabled={idx === 0}
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => {
                            if (!reorderMode) beginReorder();
                            const ids = filtered.map(item => item.id);
                            const index = idx;
                            if (index >= ids.length - 1) return;
                            const next = [...ids];
                            const [removed] = next.splice(index, 1);
                            next.splice(index + 1, 0, removed);
                            assignReorderedOrders(next);
                          }}
                          className={`p-1 rounded transition-colors ${
                            isDarkMode
                              ? "text-rp-grey/50 hover:text-rp-grey/70 hover:bg-rp-surface-raised"
                              : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                          }`}
                          title="Move down"
                          disabled={idx === filtered.length - 1}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>{q.order ?? 0}</td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}
                    >
                      {q.question}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {q.category}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {q.subcategory}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {q.type}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {new Date(q.createdDateUtc).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2 items-center">
                        <button
                          onClick={() => openEditModal(q)}
                          className={`p-1 rounded transition-colors ${
                            isDarkMode
                              ? "text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                              : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          }`}
                          title="Edit question"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setQuestionToDelete(q);
                            setDeleteConfirmOpen(true);
                          }}
                          className={`p-1 rounded transition-colors ${
                            isDarkMode
                              ? "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                              : "text-red-600 hover:text-red-700 hover:bg-red-50"
                          }`}
                          title="Delete question"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td className="px-6 py-6 text-center text-sm" colSpan={8}>
                      <span className={isDarkMode ? "text-rp-grey/50" : "text-gray-600"}>No questions found</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Suspense
        fallback={
          modalOpen ? (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <div className="p-4 rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            </div>
          ) : null
        }
      >
        <QuestionModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          question={selectedQuestion}
          mode={modalMode}
          onSave={handleSave}
          onDelete={handleDelete}
          availableCategories={categories}
          categoryToSubcategories={categoryToSubcategories}
          allQuestions={questions}
        />
      </Suspense>

      {/* Reorder Confirmation Modal */}
      <ConfirmationModal
        isOpen={reorderConfirmOpen}
        onClose={() => setReorderConfirmOpen(false)}
        onConfirm={confirmSaveReorder}
        title="Save Order Changes"
        message={`Save changes to the order of ${reorderChangesCount} question${reorderChangesCount > 1 ? "s" : ""}?`}
        confirmText="Save"
        cancelText="Cancel"
        loading={savingOrder}
      />

      {/* Delete Question Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setQuestionToDelete(null);
        }}
        onConfirm={confirmDeleteQuestion}
        title="Delete Question"
        message={`Are you sure you want to delete this question: "${questionToDelete?.question}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
        loading={deletingQuestion}
      />
      </>
      )}

      {/* Answers Tab */}
      {activeTab === "answers" && (
        <AnswersTab questions={questions} />
      )}

      {/* Manage Answers Tab */}
      {activeTab === "manage" && (
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-48">
              <div
                className={`animate-spin rounded-full h-10 w-10 border-b-2 ${
                  isDarkMode ? "border-rp-amber" : "border-blue-600"
                }`}
              ></div>
            </div>
          }
        >
          <ManageAnswersTab />
        </Suspense>
      )}
      </AdminPageLayout>
  );
};

// ==================== Answers Tab Component ====================

interface User {
  id: string;
  email: string;
  name?: string | null;
}

interface AnswersTabProps {
  questions: FormQuestion[];
}

const parseUtcTimestamp = (value?: string): number | null => {
  if (!value) return null;
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? null : ts;
};

export const AnswersTab: React.FC<AnswersTabProps> = ({ questions }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [answers, setAnswers] = useState<AdminFormResponse[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [downloading, setDownloading] = useState(false);
  const [hideInternalUsers, setHideInternalUsers] = useState(true);
  const [sortByDate, setSortByDate] = useState<"asc" | "desc">("desc");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch answers and users in parallel
      const [answersData, usersResponse] = await Promise.all([
        formService.listAllResponses(),
        import("../../services/httpService").then(mod => 
          mod.default.getAuth<{ users: User[] }>("/users?limit=10000")
        ),
      ]);
      
      setAnswers(answersData);
      setUsers(usersResponse.data.users || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch answers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create a user lookup map. The responses payload carries the authoritative
  // identity for every responder; the /users list is capped at 10000 newest
  // accounts and can miss responders, which previously rendered as "No name"
  // plus a raw user ID.
  const userMap = useMemo(() => {
    const map = new Map<string, User>();
    users.forEach(u => map.set(u.id, u));
    answers.forEach(a => {
      if (a.user) map.set(a.userId, a.user);
    });
    return map;
  }, [users, answers]);

  // Get unique categories from questions
  const categories = useMemo(() => {
    const cats = new Set<string>();
    questions.forEach(q => cats.add(q.category));
    return Array.from(cats).sort();
  }, [questions]);

  // Filter questions by category
  const filteredQuestions = useMemo(() => {
    let qs = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (filterCategory) {
      qs = qs.filter(q => q.category === filterCategory);
    }
    return qs;
  }, [questions, filterCategory]);

  const filteredQuestionIds = useMemo(() => new Set(filteredQuestions.map(q => q.id)), [filteredQuestions]);

  // Create answer lookup map: Map<`${userId}-${questionId}`, { answer, timestamp }>
  const answerLookup = useMemo(() => {
    const map = new Map<string, { answer: string; timestamp: string }>();
    answers.forEach(a => {
      map.set(`${a.userId}-${a.questionId}`, {
        answer: a.answer,
        timestamp: a.updatedDateUtc || a.createdDateUtc,
      });
    });
    return map;
  }, [answers]);

  // Get most recent answer date per user
  const userLastUpdated = useMemo(() => {
    const map = new Map<string, number>();
    answers.forEach(a => {
      if (!filteredQuestionIds.has(a.questionId)) return;
      const ts = parseUtcTimestamp(a.updatedDateUtc || a.createdDateUtc);
      if (ts === null) return;
      const existing = map.get(a.userId);
      if (existing === undefined || ts > existing) {
        map.set(a.userId, ts);
      }
    });
    return map;
  }, [answers, filteredQuestionIds]);

  // Get unique users who have answered (with user details)
  const usersWithAnswers = useMemo(() => {
    const userIds = new Set<string>();
    answers.forEach(a => userIds.add(a.userId));
    
    return Array.from(userIds)
      .map(id => {
        const user = userMap.get(id);
        return user || { id, email: id, name: null };
      })
      .filter(user => {
        // Filter out internal users if toggle is on
        if (hideInternalUsers && user.email?.toLowerCase().endsWith("@runplutus.com")) {
          return false;
        }
        return true;
      })
      .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
  }, [answers, userMap, hideInternalUsers]);

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    let result = usersWithAnswers;
    
    if (query) {
      result = result.filter(user => {
        const name = user.name?.toLowerCase() || "";
        const email = user.email?.toLowerCase() || "";
        const id = user.id.toLowerCase();
        
        // Check if user name/email matches
        if (name.includes(query) || email.includes(query) || id.includes(query)) {
          return true;
        }
        
        // Check if any of user's answers match
        for (const q of filteredQuestions) {
          const answerData = answerLookup.get(`${user.id}-${q.id}`);
          if (answerData?.answer.toLowerCase().includes(query)) {
            return true;
          }
        }
        
        return false;
      });
    }

    // Sort by date (defaults to most recent first)
    result = [...result].sort((a, b) => {
      const dateA = userLastUpdated.get(a.id);
      const dateB = userLastUpdated.get(b.id);
      if (dateA === undefined && dateB === undefined) return 0;
      if (dateA === undefined) return 1;
      if (dateB === undefined) return -1;
      return sortByDate === "desc" ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [usersWithAnswers, searchQuery, filteredQuestions, answerLookup, sortByDate, userLastUpdated]);

  // Download CSV in grid format
  const downloadCSV = async () => {
    try {
      setDownloading(true);
      
      // Build header row: User Name, User Email, Last Updated, Question1, Question2, ...
      const headers = ["User ID", "User Name", "User Email", "Last Updated", ...filteredQuestions.map(q => q.question)];
      
      // Build data rows
      const rows = filteredUsers.map(user => {
        const lastUpdated = userLastUpdated.get(user.id);
        const row: string[] = [
          user.id,
          user.name || "",
          user.email || "",
          lastUpdated !== undefined ? new Date(lastUpdated).toLocaleDateString() : "",
        ];
        
        filteredQuestions.forEach(q => {
          const answerData = answerLookup.get(`${user.id}-${q.id}`);
          row.push(answerData?.answer || "");
        });
        
        return row;
      });

      if (rows.length === 0) {
        return;
      }

      const escapeCell = (str: string) => {
        if (/[",\n\r]/.test(str)) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const csv = [
        headers.map(escapeCell).join(","),
        ...rows.map(row => row.map(escapeCell).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `form_answers_grid_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterCategory("");
    setHideInternalUsers(true);
  };

  const hasActiveFilters = searchQuery || filterCategory || !hideInternalUsers;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div
          className={`animate-spin rounded-full h-10 w-10 border-b-2 ${
            isDarkMode ? "border-rp-amber" : "border-blue-600"
          }`}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`p-4 rounded-lg ${
          isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
        }`}
      >
        <p className={`${isDarkMode ? "text-red-300" : "text-red-600"} text-sm`}>{error}</p>
        <button
          onClick={fetchData}
          className={`mt-2 text-sm ${isDarkMode ? "text-blue-400" : "text-blue-600"} hover:underline`}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Controls Row */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
              isDarkMode ? "text-rp-grey/50" : "text-gray-500"
            }`} />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search users or answers..."
              className={`pl-10 pr-3 py-2 text-sm border rounded-md w-64 focus:outline-none focus:ring-2 ${
                isDarkMode ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey placeholder-rp-grey/40" : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900"
              }`}
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`} />
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className={`px-2 py-2 text-sm border rounded-md ${
                isDarkMode ? "bg-rp-surface border-rp-separator text-rp-grey" : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Internal Users Filter Toggle */}
          <label className={`flex items-center gap-2 cursor-pointer text-sm ${
            isDarkMode ? "text-rp-grey/70" : "text-gray-600"
          }`}>
            <input
              type="checkbox"
              checked={hideInternalUsers}
              onChange={e => setHideInternalUsers(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Hide internal users
          </label>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className={`px-2 py-2 text-sm rounded-md flex items-center gap-1 ${
                isDarkMode
                  ? "text-rp-grey/70 hover:text-rp-grey hover:bg-rp-surface-raised"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className={`px-3 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center ${
              isDarkMode
                ? "text-rp-grey/70 hover:text-rp-grey hover:bg-rp-surface-raised"
                : "text-gray-600 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={downloadCSV}
            disabled={downloading || filteredUsers.length === 0}
            className={`px-3 py-2 rounded-lg transition-colors flex items-center ${
              isDarkMode ? "bg-green-600 hover:bg-green-700 text-white" : "bg-green-600 hover:bg-green-700 text-white"
            } disabled:opacity-50`}
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Preparing..." : "Download CSV"}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className={`mb-4 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
        Showing {filteredUsers.length} users x {filteredQuestions.length} questions
        {hasActiveFilters && " (filtered)"}
      </div>

      {/* Grid Table */}
      <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} shadow rounded-lg overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <thead className={isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}>
              <tr>
                {/* User column header - sticky */}
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky left-0 z-10 ${
                    isDarkMode ? "text-rp-grey/70 bg-rp-surface-sunken" : "text-gray-500 bg-gray-50"
                  }`}
                  style={{ minWidth: "200px" }}
                >
                  User
                </th>
                {/* Last Updated column header - sortable */}
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none ${
                    isDarkMode ? "text-rp-grey/70 hover:bg-rp-surface-raised" : "text-gray-500 hover:bg-gray-100"
                  }`}
                  style={{ minWidth: "110px" }}
                  onClick={() => setSortByDate(prev => (prev === "desc" ? "asc" : "desc"))}
                >
                  <div className="flex items-center gap-1">
                    Last Updated
                    {sortByDate === "desc" && <ArrowDown className="w-3 h-3" />}
                    {sortByDate === "asc" && <ArrowUp className="w-3 h-3" />}
                  </div>
                </th>
                {/* Question column headers */}
                {filteredQuestions.map(q => (
                  <th
                    key={q.id}
                    className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                    }`}
                    style={{ minWidth: "180px", maxWidth: "250px" }}
                    title={q.question}
                  >
                    <div className="flex flex-col">
                      <span className="truncate">{q.question.length > 40 ? q.question.substring(0, 40) + "..." : q.question}</span>
                      <span className={`text-xs font-normal normal-case ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
                        {q.category}{q.subcategory ? ` / ${q.subcategory}` : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`${isDarkMode ? "bg-rp-surface divide-rp-separator" : "bg-white divide-gray-200"} divide-y`}>
              {filteredUsers.map(user => (
                <tr key={user.id} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
                  {/* User cell - sticky */}
                  <td
                    className={`px-4 py-3 whitespace-nowrap sticky left-0 z-10 ${
                      isDarkMode ? "bg-rp-surface" : "bg-white"
                    }`}
                    style={{ minWidth: "200px" }}
                  >
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                        isDarkMode ? "bg-rp-surface-raised" : "bg-gray-200"
                      }`}>
                        <span className={`text-xs font-medium ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                          {(user.name || user.email || "U").charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3">
                        <div className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                          {user.name || "No name"}
                        </div>
                        <div className={`text-xs ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                          {user.email || user.id.substring(0, 8) + "..."}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Last Updated cell */}
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-600"}`}>
                    {userLastUpdated.get(user.id) !== undefined
                      ? new Date(userLastUpdated.get(user.id)!).toLocaleDateString()
                      : "-"}
                  </td>
                  {/* Answer cells */}
                  {filteredQuestions.map(q => {
                    const answerData = answerLookup.get(`${user.id}-${q.id}`);
                    return (
                      <td
                        key={q.id}
                        className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey" : "text-gray-800"}`}
                        style={{ minWidth: "180px", maxWidth: "250px" }}
                      >
                        {answerData ? (
                          <div className="max-w-full" title={answerData.answer}>
                            {answerData.answer.length > 60 ? answerData.answer.substring(0, 60) + "..." : answerData.answer}
                          </div>
                        ) : (
                          <span className={`italic ${isDarkMode ? "text-rp-grey/25" : "text-gray-400"}`}>-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td
                    className="px-4 py-6 text-center text-sm"
                    colSpan={filteredQuestions.length + 2}
                  >
                    <span className={isDarkMode ? "text-rp-grey/50" : "text-gray-600"}>
                      {hasActiveFilters ? "No users match your filters" : "No answers found"}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuestionsPage;
